// @ts-nocheck
// app/guides/index.tsx — Editorial guides hub

import { Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { Stack, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { ArrowLeft } from 'lucide-react-native';

import { theme, ACCENT, INK, INK_MUTED, INK_SOFT, PAPER, PAPER_SOFT } from '@/constants/theme';
import {
  DoubleRule, EditorialButton, EditorialFooter, Eyebrow, Headline, Numeral, Paper, PageHero, Section, useEditorialBreakpoint,
} from '@/components/editorial';
import { EditorialHeader } from '@/components/editorial/EditorialHeader';
import { AccessibilityButton } from '@/components/AccessibilityButton';

const MONO = theme.fonts.mono;
const SERIF = theme.fonts.serif;
const SERIF_BOLD = theme.fonts.serifBold;
const SERIF_BLACK = theme.fonts.serifBlack;
const SANS = theme.fonts.regular;
const IS_WEB = Platform.OS === 'web';
const SITE_URL = 'https://kursiaii.com';
const PAGE_URL = `${SITE_URL}/guides`;

const GUIDE_CARDS = [
  { href: '/guides/by-goal',                title: 'מדריך לפי מטרה',         body: 'בחירת קורס לפי יעד אמיתי: הסבת קריירה, קידום מקצועי, הכנסה נוספת או שדרוג מיומנות.' },
  { href: '/guides/by-city',                title: 'מדריך לפי עיר',          body: 'השוואת קורסים פרונטליים והיברידיים לפי אזור גיאוגרפי — לקצר את זמן החיפוש.' },
  { href: '/guides/by-budget',              title: 'מדריך לפי תקציב',        body: 'איך לבחור קורס שמתאים למסגרת התקציב בלי ליפול על מסלול יקר מדי או חלש מדי.' },
  { href: '/guides/beginners-programming',  title: 'מדריך למתחילים בתכנות',  body: 'מסלול כניסה ברור לעולם התכנות: מה ללמוד ראשון, איך לבדוק קורסים ואיך לא לבזבז חודשים.' },
];

const HUB_LINKS = [
  { href: '/courses',  title: 'קטגוריות קורסים', body: '16 קטגוריות לימוד עם מדריך עורך וצ׳ק־ליסט החלטה.' },
  { href: '/articles', title: 'מרכז המאמרים',    body: 'מאמרי עומק, השוואות וטיפים שמגיעים בשבועיים.' },
];

export default function GuidesHubPage() {
  const router = useRouter();
  const { isDesktop } = useEditorialBreakpoint();
  const breadcrumbSchema = {
    '@context': 'https://schema.org', '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'דף הבית', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'מדריכי בחירת קורסים', item: PAGE_URL },
    ],
  };
  const collectionSchema = {
    '@context': 'https://schema.org', '@type': 'CollectionPage',
    name: 'מדריכי בחירת קורסים',
    description: 'מרכז מדריכי SEO לבחירת קורסים לפי מטרה, עיר, תקציב ורמת ניסיון.',
    url: PAGE_URL, inLanguage: 'he',
    hasPart: GUIDE_CARDS.map(g => ({ '@type': 'WebPage', name: g.title, url: `${SITE_URL}${g.href}`, description: g.body })),
  };

  return (
    <Paper>
      <AccessibilityButton />
      <Stack.Screen options={{ title: 'מדריכים · Kursi' }} />
      {IS_WEB && (
        <Head>
          <title>מדריכי בחירה · Kursi</title>
          <meta name="description" content="מרכז מדריכים לבחירת קורסים — לפי מטרה, עיר, תקציב, ורמת ניסיון. בעברית, בלי שטויות." />
          <link rel="canonical" href={PAGE_URL} />
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionSchema) }} />
        </Head>
      )}

      <ScrollView style={{ flex: 1, backgroundColor: PAPER }} contentContainerStyle={{ backgroundColor: PAPER }}>
        <EditorialHeader />

        <Section py="xl" px="md" max={1240}>
          <PageHero
            kicker="גיליון המדריכים"
            title={<>איך בוחרים קורס{'\n'}<Text style={{ color: ACCENT, fontStyle: 'italic' as any }}>בלי לבזבז חודשים.</Text></>}
            lede="ארבעה מדריכי עורך לבחירת קורס: לפי מטרה, לפי עיר, לפי תקציב, ולפי מצב ההתחלה שלכם."
            meta={`${GUIDE_CARDS.length} מדריכים · ${HUB_LINKS.length} מרכזים נוספים`}
          />
        </Section>

        <Section py="lg" px="md" max={1240}>
          <View style={s.grid}>
            {GUIDE_CARDS.map((g, i) => (
              <Pressable
                key={g.href}
                onPress={() => router.push(g.href as any)}
                style={({ hovered, pressed }: any) => [
                  s.card, { width: isDesktop ? '50%' : '100%' },
                  hovered && s.cardHover, pressed && { opacity: 0.92 },
                ]}
                accessibilityRole="link"
              >
                <View style={s.cardInner}>
                  <View style={{ flexDirection: 'row-reverse', justifyContent: 'space-between', alignItems: 'flex-start' } as any}>
                    <Numeral size={64}>{String(i + 1).padStart(2, '0')}</Numeral>
                    <ArrowLeft color={INK} size={18} />
                  </View>
                  <View style={{ height: 16 }} />
                  <Text style={s.cardTitle}>{g.title}</Text>
                  <View style={{ height: 8 }} />
                  <Text style={s.cardBody}>{g.body}</Text>
                </View>
              </Pressable>
            ))}
          </View>
        </Section>

        <Section py="md" px="md" max={1240}><DoubleRule /></Section>

        <Section py="xl" px="md" max={1240}>
          <Eyebrow>מרכזים נוספים</Eyebrow>
          <View style={{ height: 14 }} />
          <Headline level={2}>אחרי המדריכים — לכאן.</Headline>
          <View style={{ height: 28 }} />
          <View style={s.hubRow}>
            {HUB_LINKS.map((l) => (
              <Pressable
                key={l.href}
                onPress={() => router.push(l.href as any)}
                style={({ hovered }: any) => [s.hubCard, hovered && { backgroundColor: INK }]}
                accessibilityRole="link"
              >
                {({ hovered }: any) => (
                  <View>
                    <Text style={[s.hubTitle, hovered && { color: PAPER }]}>{l.title}</Text>
                    <View style={{ height: 8 }} />
                    <Text style={[s.hubBody, hovered && { color: PAPER_SOFT }]}>{l.body}</Text>
                    <View style={{ height: 14 }} />
                    <Text style={[s.hubArrow, hovered && { color: ACCENT }]}>קרא ←</Text>
                  </View>
                )}
              </Pressable>
            ))}
          </View>
        </Section>

        <EditorialFooter onNavigate={(href) => router.push(href as any)} />
      </ScrollView>
    </Paper>
  );
}

const s = StyleSheet.create({
  grid: { flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 0 } as any,
  card: { padding: 0, marginLeft: -1, marginTop: -1 } as any,
  cardInner: {
    borderWidth: 1, borderColor: INK, padding: 28, backgroundColor: PAPER_SOFT,
    ...(IS_WEB ? { transition: 'all 220ms ease' } as any : {}),
  },
  cardHover: {},
  cardTitle: { fontFamily: SERIF_BOLD, fontSize: 28, color: INK, letterSpacing: -0.5, textAlign: 'right' },
  cardBody: { fontFamily: SANS, fontSize: 15, lineHeight: 24, color: INK_SOFT, textAlign: 'right' },

  hubRow: { flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 0 } as any,
  hubCard: {
    flex: 1, minWidth: 280, padding: 28,
    borderWidth: 1, borderColor: INK,
    marginLeft: -1, backgroundColor: 'transparent',
    ...(IS_WEB ? { transition: 'all 220ms ease' } as any : {}),
  } as any,
  hubTitle: { fontFamily: SERIF_BOLD, fontSize: 26, color: INK, letterSpacing: -0.4, textAlign: 'right' },
  hubBody: { fontFamily: SANS, fontSize: 14, lineHeight: 22, color: INK_SOFT, textAlign: 'right' },
  hubArrow: { fontFamily: MONO, fontSize: 11, color: ACCENT, letterSpacing: 1.4, textTransform: 'uppercase' as any, textAlign: 'right' },
});
